import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Alert, 
  Paper, 
  InputAdornment, 
  IconButton 
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { apiService, ErreurTropDeTentatives } from '../../services/ApiService';

/**
 * « Réessayez dans 3 minutes » plutôt que « 174 secondes ».
 */
function formaterAttente(secondes: number): string {
  if (secondes < 60) {
    return `${secondes} seconde${secondes > 1 ? 's' : ''}`;
  }
  const minutes = Math.ceil(secondes / 60);
  return `${minutes} minute${minutes > 1 ? 's' : ''}`;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const success = await apiService.login({ username, password });
      
      if (success) {
        // Rediriger vers la page d'origine ou la page d'accueil
        navigate(from, { replace: true });
      } else {
        setError("Échec de l'authentification. Veuillez vérifier vos identifiants.");
      }
    } catch (err) {
      // Une temporisation n'est PAS un mauvais mot de passe. Afficher
      // « vérifiez vos identifiants » ici pousse l'opérateur à retaper son mot
      // de passe en boucle, ce qui allonge sa propre temporisation.
      if (err instanceof ErreurTropDeTentatives) {
        setError(
          `Trop de tentatives de connexion. Le compte est temporisé pendant ` +
          `${formaterAttente(err.secondes)}. Toute tentative avant la fin de ce ` +
          `délai sera refusée.`
        );
      } else {
        setError("Une erreur est survenue lors de la connexion.");
        console.error(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            SONDE-THERMO
          </Typography>
          <Typography component="h2" variant="h6" align="center" color="textSecondary" sx={{ mb: 3 }}>
            Connexion
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Nom d'utilisateur"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Mot de passe"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
            >
              {isLoading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
